import { useEffect, useRef } from 'react';

import { type ChosenUnit, unitAway, unitLabel, type UnitOption } from '../api/jobs.ts';
import { Button } from '../components/button.tsx';

// CB-REQ INV-11 at v1.948: which serials go out on a job line. A serialised line is reserved by type
// at acceptance and its units are chosen here, one at a time; bulk lines go out by quantity and have
// nothing to choose. The database decides what may be chosen — this shows what it will say, so a
// person is not offered a unit in repair or one another job already has over these dates.

/** The serials chosen so far, each with where it is now and a way to take it off the line. */
export function ChosenSerials({
  allocated,
  quantity,
  mayAllocate,
  pending,
  onRelease,
}: {
  allocated: readonly ChosenUnit[];
  quantity: number;
  mayAllocate: boolean;
  pending: boolean;
  onRelease: (assetId: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm">
        {allocated.length} of {quantity} chosen
      </span>
      {allocated.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5" aria-label="Chosen serials">
          {allocated.map((u) => {
            // WHS-16 at v1.949: once it has gone, where it got to matters more than where it is.
            const progress = progressOf(u);
            const away = u.dispatch === 'allocated' ? unitAway(u) : null;
            const flagged = away !== null || u.condition === 'damaged' || u.condition === 'missing';
            return (
              <li key={u.assetId} className="flex items-center gap-1">
                <span className={flagged ? 'tag tag-accent-2' : 'tag'}>
                  {unitLabel(u)}
                  {away ? ` · ${away}` : ''}
                  {progress ? ` · ${progress}` : ''}
                </span>
                {mayAllocate && u.dispatch === 'allocated' ? (
                  <button
                    type="button"
                    className="link text-xs"
                    disabled={pending}
                    aria-label={`Remove ${unitLabel(u)}`}
                    onClick={() => onRelease(u.assetId)}
                  >
                    Remove
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

/** The units of the line's product, and whether each may be chosen for this job. */
export function SerialPicker({
  options,
  gone = new Set(),
  remaining,
  pending,
  error,
  onChoose,
  onClose,
}: {
  options: readonly UnitOption[];
  /** Chosen units of this line already out or back: said as such, not as "Chosen". */
  gone?: ReadonlySet<string>;
  /** How many units of the line still have no serial. */
  remaining: number;
  pending: boolean;
  error: Error | null;
  onChoose: (assetId: string) => void;
  onClose: () => void;
}) {
  const panel = useRef<HTMLDivElement>(null);
  // It opens below the lines; on a phone that is below the fold, so it is brought into view.
  useEffect(() => {
    panel.current?.scrollIntoView?.({ block: 'nearest' });
  }, []);
  return (
    <div ref={panel} className="flex flex-col gap-3 border-2 border-divider p-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold">
          {remaining > 0
            ? `Choose ${remaining} more ${remaining === 1 ? 'unit' : 'units'}`
            : 'Every unit on this line has its serial.'}
        </p>
        <Button type="button" variant="quiet" onClick={onClose}>
          Done
        </Button>
      </div>
      <ul className="flex flex-col divide-y-2 divide-divider" aria-label="Units">
        {options.map((o) => (
          <li key={o.assetId} className="flex items-center justify-between gap-3 py-1.5">
            <span>
              <span className="font-semibold">{unitLabel(o)}</span>
              {o.serialNumber && o.assetTag ? (
                <span className="text-sm text-muted"> · S/N {o.serialNumber}</span>
              ) : null}
              {/* INV-A12 at v1.955: where it is, so the person knows which shelf to pick from. */}
              {o.keptAt && !o.keptElsewhere ? (
                <span className="text-sm text-muted"> · {o.keptAt}</span>
              ) : null}
            </span>
            {gone.has(o.assetId) ? (
              <span className="tag">Sent</span>
            ) : (
              <UnitAction option={o} remaining={remaining} pending={pending} onChoose={onChoose} />
            )}
          </li>
        ))}
      </ul>
      {error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {error.message}
        </p>
      ) : null}
    </div>
  );
}

function UnitAction({
  option,
  remaining,
  pending,
  onChoose,
}: {
  option: UnitOption;
  remaining: number;
  pending: boolean;
  onChoose: (assetId: string) => void;
}) {
  // Each reason is said in words rather than shown as a greyed button (CB-REQ ADO-06).
  if (option.chosenHere) return <span className="tag tag-accent">Chosen</span>;
  const away = unitAway(option);
  if (away) return <span className="text-sm text-muted">{away}</span>;
  if (option.committedElsewhere) {
    return (
      <span className="text-sm text-muted">
        {option.committedTo ? `On ${option.committedTo}` : 'Held firmly on a quote'}
      </span>
    );
  }
  // INV-A12 at v1.955: a serial is chosen only where it is kept; this one would be moved first.
  if (option.keptElsewhere) {
    return <span className="text-sm text-muted">Kept at {option.keptElsewhere}</span>;
  }
  if (remaining === 0) return null;
  return (
    <Button
      type="button"
      variant="quiet"
      disabled={pending}
      aria-label={`Choose ${unitLabel(option)}`}
      onClick={() => onChoose(option.assetId)}
    >
      Choose
    </Button>
  );
}

function progressOf(u: ChosenUnit): string | null {
  if (u.dispatch === 'out') return 'Out';
  if (u.dispatch !== 'returned') return null;
  if (u.handedOnTo) return `On to ${u.handedOnTo.reference}`;
  return u.condition === 'damaged' ? 'Damaged' : u.condition === 'missing' ? 'Missing' : 'Back';
}
